<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import socketClient from '@/services/socketClient';
import AudioPlayer from '@/components/crm/atendimento/AudioPlayer.vue';
import CanalComposer from '@/components/crm/canais/CanalComposer.vue';
import CropFotoModal from '@/components/crm/canais/CropFotoModal.vue';
import resolveUrl from '@/services/resolveUrl';
import linkify from '@/services/linkify';

// Emojis do picker de enquete/teste (as opções aceitam emoji, que é só texto).
const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😅', '😊', '😇', '🙂', '😉', '😍', '😘', '🥰', '😎', '🤩', '🥳',
  '🤔', '🤨', '😐', '😶', '🙄', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😢', '😭', '😤', '😠', '😡',
  '👍', '👎', '👏', '🙌', '🤝', '👋', '🙏', '💪', '👌', '✌️', '🤞', '✊',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '✨', '🔥', '⭐', '🎉', '🎊', '🎁',
  '✅', '❌', '⚠️', '❗', '❓', '💯', '🆗', '🆘', '🏆', '🥇', '🥈', '🥉', '📊', '📈', '🧪', '💡',
];

function formatarHora(d) {
  if (!d) return '';
  try {
    const data = new Date(d);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === hoje.toDateString()) {
      return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (data.toDateString() === ontem.toDateString()) return 'Ontem';
    return data.toLocaleDateString('pt-BR');
  } catch (_e) { return ''; }
}

function formatarHoraCompleta(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch (_e) { return ''; }
}

function dataSeparador(d) {
  if (!d) return '';
  try {
    const data = new Date(d);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === hoje.toDateString()) return 'Hoje';
    if (data.toDateString() === ontem.toDateString()) return 'Ontem';
    return data.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
  } catch (_e) { return ''; }
}

// Canal (WhatsApp) escolhido para postar em cada newsletter que está em mais
// de um número conectado. Guarda `jid -> id da linha`. Por navegador.
const CANAL_KEY = 'newsletters_canal_por_jid';
function lerCanaisSalvos() {
  try {
    const v = JSON.parse(window.localStorage.getItem(CANAL_KEY) || '{}');
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  } catch (_e) { return {}; }
}

export default {
  name: 'CanaisIndex',
  components: { Layout, PageHeader, AudioPlayer, CanalComposer, CropFotoModal },
  data() {
    return {
      canais: [],
      baileys: [],
      filtros: { canal_baileys_id: '', busca: '', somenteAdmin: false, incluirArquivados: false },
      carregando: true,
      erro: null,
      sucesso: null,
      sincronizandoTodos: false,
      // Modais
      modalImportar: false,
      importando: false,
      importar: { canal_baileys_id: '', identificador: '', seguir: true },
      modalCriar: false,
      criando: false,
      novoCanal: { canal_baileys_id: '', nome: '', descricao: '' },
      // Enquete / Teste (quiz). Duas opções em branco já na abertura.
      modalInterativo: {
        visivel: false, quiz: false, pergunta: '', opcoes: ['', ''], multipla: false, correta: 0, enviando: false,
      },
      // Picker de emoji do modal (insere na pergunta ou numa opção).
      emojis: EMOJIS,
      emojiPicker: { aberto: false, campo: null, i: -1 },
      // Canal ativo
      canalAtivoId: null,
      // jid -> id da linha escolhida pelo usuário para postar.
      canalPorJid: lerCanaisSalvos(),
      mensagens: [],
      carregandoMensagens: false,
      // Painel info
      painelInfoAberto: false,
      sincronizandoAtivo: false,
      // Edição rápida (no painel info)
      editandoNome: false,
      novoNome: '',
      editandoDescricao: false,
      novaDescricao: '',
      enviandoFoto: false,
      cropFoto: { visivel: false, file: null, enviando: false, erro: null },
      // Lightbox
      lightbox: null,
      retry: {},
      socketUnsubscribers: [],
    };
  },
  computed: {
    canaisConectadosIds() {
      return new Set(this.baileys.filter((c) => c.status === 'conectado').map((c) => Number(c.id)));
    },
    baileysConectados() {
      return this.baileys.filter((c) => c.status === 'conectado');
    },
    /**
     * Linhas visíveis agrupadas por JID.
     *
     * O sync grava UMA linha por (número, canal do WhatsApp): a mesma
     * newsletter vista por dois WhatsApps virava duas linhas iguais na tela.
     */
    ocorrenciasPorJid() {
      const mapa = new Map();
      for (const c of this.canais) {
        if (!this.canaisConectadosIds.has(Number(c.canal_baileys_id))) continue;
        if (!mapa.has(c.jid)) mapa.set(c.jid, []);
        mapa.get(c.jid).push(c);
      }
      return mapa;
    },
    ocorrenciasDoAtivo() {
      if (!this.canalAtivo) return [];
      return this.ocorrenciasPorJid.get(this.canalAtivo.jid) || [];
    },
    canaisFiltrados() {
      const t = (this.filtros.busca || '').trim().toLowerCase();
      const lista = this.canais.filter((c) => {
        // Só exibe se o canal API-B correspondente está conectado
        if (!this.canaisConectadosIds.has(Number(c.canal_baileys_id))) return false;
        if (!this.filtros.incluirArquivados && c.arquivado) return false;
        if (this.filtros.somenteAdmin && !c.is_admin) return false;
        if (this.filtros.canal_baileys_id && Number(c.canal_baileys_id) !== Number(this.filtros.canal_baileys_id)) return false;
        if (t && !(c.nome || '').toLowerCase().includes(t)) return false;
        return true;
      });
      // Um canal, uma linha. A escolha sai de dentro do que já passou pelos
      // filtros — filtrar por número continua mostrando aquele número.
      const porJidVisivel = new Map();
      for (const c of lista) {
        if (!porJidVisivel.has(c.jid)) porJidVisivel.set(c.jid, []);
        porJidVisivel.get(c.jid).push(c);
      }
      const unicos = [...porJidVisivel.values()].map((ocorrencias) => this.escolherOcorrencia(ocorrencias));

      // Ordena por última atividade (descendente). Sem atividade, usa updated_at.
      const tsDe = (c) => new Date(c.ultima_mensagem_em || c.updated_at || 0).getTime();
      return unicos.sort((a, b) => tsDe(b) - tsDe(a));
    },
    canalAtivo() {
      return this.canais.find((c) => c.id === this.canalAtivoId) || null;
    },
    isAdminAtivo() {
      return Boolean(this.canalAtivo && this.canalAtivo.is_admin);
    },
    interativoOpcoesValidas() {
      const limpas = this.modalInterativo.opcoes.map((o) => (o || '').trim()).filter(Boolean);
      const semRepetir = new Set(limpas.map((o) => o.toLowerCase())).size === limpas.length;
      return limpas.length >= 2 && limpas.length <= 12 && semRepetir;
    },
    interativoValido() {
      const p = (this.modalInterativo.pergunta || '').trim();
      const base = p.length > 0 && p.length <= 255 && this.interativoOpcoesValidas;
      if (!this.modalInterativo.quiz) return base;
      // Teste exige uma resposta correta apontando para uma opção preenchida.
      const idx = this.modalInterativo.correta;
      const opcaoOk = Number.isInteger(idx) && idx >= 0 && idx < this.modalInterativo.opcoes.length
        && Boolean((this.modalInterativo.opcoes[idx] || '').trim());
      return base && opcaoOk;
    },
    linkCompartilhamentoAtivo() {
      const c = this.canalAtivo;
      if (!c || !c.invite_code) return null;
      return `https://whatsapp.com/channel/${c.invite_code}`;
    },
    mensagensComSeparador() {
      const out = [];
      let ultimaData = '';
      for (const m of this.mensagens) {
        const d = m.mensagem_em ? new Date(m.mensagem_em).toDateString() : '';
        if (d && d !== ultimaData) {
          out.push({ separador: true, label: dataSeparador(m.mensagem_em), id: `sep-${d}` });
          ultimaData = d;
        }
        out.push(m);
      }
      return out;
    },
  },
  async mounted() {
    document.body.classList.add('canais-sem-footer');
    this._onEsc = (e) => { if (e.key === 'Escape' && this.lightbox) this.fecharLightbox(); };
    document.addEventListener('keydown', this._onEsc);
    // Back nativo do celular: fecha o chat em vez de trocar de rota.
    this._chatHistoryPushed = false;
    this._boundPopState = this._onPopState.bind(this);
    window.addEventListener('popstate', this._boundPopState);
    await Promise.all([this.carregarBaileys(), this.carregar()]);

    // Pré-seleção: ?id=N na URL ou sessionStorage (vindo de /canais/detalhe legado).
    let idPre = this.$route.query.id;
    if (!idPre) {
      try {
        idPre = sessionStorage.getItem('canal_pre_selecionado');
        if (idPre) sessionStorage.removeItem('canal_pre_selecionado');
      } catch (_e) { /* noop */ }
    }
    if (idPre) {
      const c = this.canais.find((x) => Number(x.id) === Number(idPre));
      if (c) this.abrirChat(c);
    }

    const u = JSON.parse(localStorage.getItem('userdata') || '{}');
    socketClient.connect(u && u.id);
    this.socketUnsubscribers.push(
      socketClient.on('baileys:newsletter-mensagem', (payload) => this.onNovaMensagem(payload)),
      socketClient.on('baileys:newsletter-mensagem-atualizada', (payload) => this.onMensagemAtualizada(payload)),
      socketClient.on('baileys:newsletter-lido', (payload) => {
        if (!payload || !payload.newsletterId) return;
        const c = this.canais.find((x) => x.id === payload.newsletterId);
        if (c) c.nao_lidas_count = 0;
      }),
      socketClient.on('baileys:status', (payload) => {
        if (!payload || !payload.canalId) return;
        const c = this.baileys.find((x) => x.id === payload.canalId);
        if (c) {
          c.status = payload.status;
          if (payload.telefone) c.telefone = payload.telefone;
        }
        if (payload.status !== 'conectado' && this.canalAtivo && this.canalAtivo.canal_baileys_id === payload.canalId) {
          this.fecharChat();
          this.erro = 'O canal API-B deste canal foi desconectado.';
        }
      }),
    );
  },
  beforeUnmount() {
    document.body.classList.remove('canais-sem-footer');
    document.body.classList.remove('canais-chat');
    document.body.style.overflow = '';
    if (this._onEsc) document.removeEventListener('keydown', this._onEsc);
    if (this._boundPopState) window.removeEventListener('popstate', this._boundPopState);
    this.socketUnsubscribers.forEach((fn) => { try { fn(); } catch (_e) { /* noop */ } });
  },
  methods: {
    formatarHora,
    formatarHoraCompleta,
    linkify,

    async carregarBaileys() {
      try {
        const { data } = await axios.get('/canal/baileys', { params: { order: JSON.stringify([['nome', 'asc']]) } });
        // Só canais marcados como tipo "operacao" entram aqui. Grupos/newsletters
        // vinculados a canais sem "operacao" caem por consequência (canaisConectadosIds
        // não os inclui). Linhas com mesmo JID em outros canais aparecem normalmente.
        const lista = Array.isArray(data) ? data : [];
        this.baileys = lista.filter((c) => Array.isArray(c.tipos) && c.tipos.includes('operacao'));
      } catch (error) {
        this.erro = `Falha ao listar canais API-B: ${error.message}`;
      }
    },

    /**
     * Qual das linhas do mesmo canal representa a conversa: a escolhida pelo
     * usuário > a do número que é admin (o único que consegue postar) > a de
     * atividade mais recente. Desempate por id mantém a lista estável.
     */
    escolherOcorrencia(ocorrencias = []) {
      if (ocorrencias.length <= 1) return ocorrencias[0];

      const escolhidoId = Number(this.canalPorJid[ocorrencias[0].jid]);
      const escolhido = ocorrencias.find((c) => Number(c.id) === escolhidoId);
      if (escolhido) return escolhido;

      const ts = (c) => new Date(c.ultima_mensagem_em || 0).getTime();
      return [...ocorrencias].sort((a, b) => {
        if (a.is_admin !== b.is_admin) return a.is_admin ? -1 : 1;
        if (ts(a) !== ts(b)) return ts(b) - ts(a);
        return a.id - b.id;
      })[0];
    },

    /** Troca o WhatsApp por onde este canal é lido e postado. */
    async trocarCanalDoJid(ocorrencia) {
      if (!ocorrencia || ocorrencia.id === this.canalAtivoId) return;
      this.canalPorJid = { ...this.canalPorJid, [ocorrencia.jid]: ocorrencia.id };
      try {
        window.localStorage.setItem(CANAL_KEY, JSON.stringify(this.canalPorJid));
      } catch (_e) { /* modo privado: a escolha vale só nesta sessão */ }

      this.canalAtivoId = ocorrencia.id;
      this.mensagens = [];
      await this.carregarMensagens();
    },

    nomeDoBaileys(c) {
      return (c && c.canal && (c.canal.nome || c.canal.telefone)) || 'número';
    },
    /** Em quantos WhatsApps conectados este canal está. 1 = sem ambiguidade. */
    baileysDoCanal(c) {
      return (c && this.ocorrenciasPorJid.get(c.jid)) || [];
    },
    resumoBaileysDoCanal(c) {
      const nomes = this.baileysDoCanal(c).map((o) => this.nomeDoBaileys(o));
      return `Canal presente em ${nomes.length} números: ${nomes.join(', ')}`;
    },

    async carregar() {
      this.carregando = true;
      try {
        const params = { order: JSON.stringify([['nome', 'asc']]), limit: 500 };
        if (this.filtros.canal_baileys_id) {
          params.where = JSON.stringify({ canal_baileys_id: Number(this.filtros.canal_baileys_id) });
        }
        params.include = JSON.stringify([{ model: 'CanalBaileys', as: 'canal' }]);
        const { data } = await axios.get('/newsletter', { params });
        this.canais = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao listar canais: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregando = false;
      }
    },

    _ehMobile() {
      return window.matchMedia('(max-width: 767.98px)').matches;
    },
    async abrirChat(c) {
      if (this.canalAtivoId === c.id) return;
      const eraLista = this.canalAtivoId == null;
      this.canalAtivoId = c.id;
      this.mensagens = [];
      this.painelInfoAberto = false;
      this.editandoNome = false;
      this.editandoDescricao = false;
      this.novoNome = c.nome || '';
      this.novaDescricao = c.descricao || '';
      // Conversa em fullscreen no mobile: esconde a topbar do app (via CSS).
      document.body.classList.add('canais-chat');
      // Mobile: empurra um estado no histórico para o "voltar" nativo do
      // celular fechar o chat (voltar para a lista) em vez de trocar de rota.
      // Só empurra ao SAIR da lista (não a cada troca de canal).
      if (eraLista && this._ehMobile()) {
        window.history.pushState({ canalChat: true }, '');
        this._chatHistoryPushed = true;
      }
      // Sempre re-sincroniza ao abrir pra trazer estado fresco (papel, descrição, contagem)
      this.sincronizarSilencioso(c.id);
      await this.carregarMensagens();
      this.$nextTick(() => this.scrollChatParaFim());
      this.marcarComoLido(c.id);
    },

    _fecharChatEstado() {
      this.canalAtivoId = null;
      this.mensagens = [];
      this.painelInfoAberto = false;
      document.body.classList.remove('canais-chat');
    },
    fecharChat() {
      // Se empurramos um estado no histórico (mobile), volta por ele — o
      // popstate fecha o chat. Mantém histórico e back nativo consistentes.
      if (this._chatHistoryPushed) {
        window.history.back();
      } else {
        this._fecharChatEstado();
      }
    },
    _onPopState() {
      // Voltar nativo/navegador com um chat aberto → fecha o chat (volta à lista).
      if (this.canalAtivoId != null) {
        this._chatHistoryPushed = false;
        this._fecharChatEstado();
      }
    },

    async carregarMensagens() {
      if (!this.canalAtivoId) return;
      this.carregandoMensagens = true;
      try {
        const { data } = await axios.get(`/newsletter/mensagens/${this.canalAtivoId}`, { params: { limit: 100 } });
        this.mensagens = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao carregar posts: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregandoMensagens = false;
      }
    },

    async sincronizarSilencioso(id) {
      try {
        const { data } = await axios.post(`/newsletter/sincronizar/${id}`);
        const idx = this.canais.findIndex((x) => x.id === id);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], ...data });
      } catch (_e) { /* best-effort */ }
    },

    async sincronizar(c) {
      try {
        const { data } = await axios.post(`/newsletter/sincronizar/${c.id}`);
        const idx = this.canais.findIndex((x) => x.id === c.id);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], ...data });
        this.sucesso = `${data.nome || 'Canal'} sincronizado.`;
      } catch (error) {
        this.erro = `Falha ao sincronizar: ${error.response?.data?.error || error.message}`;
      }
    },

    async sincronizarAtivo() {
      if (!this.canalAtivo) return;
      this.sincronizandoAtivo = true;
      try {
        await this.sincronizar(this.canalAtivo);
      } finally { this.sincronizandoAtivo = false; }
    },

    async marcarComoLido(id) {
      const c = this.canais.find((x) => x.id === id);
      if (!c || !c.nao_lidas_count) return;
      try {
        await axios.post(`/newsletter/marcarLido/${id}`);
        c.nao_lidas_count = 0;
      } catch (_e) { /* best-effort */ }
    },

    onNovaMensagem(payload) {
      if (!payload || !payload.newsletterId) return;
      // Atualiza prévia + contador na lista
      const c = this.canais.find((x) => x.id === payload.newsletterId);
      if (c) {
        c.ultima_mensagem_em = payload.mensagem_em || new Date().toISOString();
        c.ultima_mensagem_preview = payload.preview || payload.conteudo || null;
        c.ultima_mensagem_tipo = payload.tipo || null;
        c.ultima_mensagem_direcao = payload.direcao || null;
        if (typeof payload.nao_lidas_count === 'number') {
          c.nao_lidas_count = payload.nao_lidas_count;
        }
      }

      if (payload.newsletterId !== this.canalAtivoId) return;

      if (c && c.nao_lidas_count && payload.direcao === 'entrada') {
        this.marcarComoLido(payload.newsletterId);
      }

      // Dedup defensivo por external_id
      if (payload.external_id) {
        const dup = this.mensagens.find((m) => m.external_id === payload.external_id);
        if (dup) {
          // Eventos de mídia que chegaram após nossa persistência local: só completa midia
          if (payload.midia_url && !dup.midia_url) {
            const idx = this.mensagens.indexOf(dup);
            this.mensagens.splice(idx, 1, { ...dup, midia_url: payload.midia_url, midia_meta: payload.midia_meta || dup.midia_meta });
          }
          return;
        }
      }

      this.mensagens.push({
        id: payload.id || `live-${Date.now()}-${Math.random()}`,
        newsletter_id: payload.newsletterId,
        direcao: payload.direcao,
        tipo: payload.tipo,
        conteudo: payload.conteudo,
        midia_url: payload.midia_url || null,
        midia_meta: payload.midia_meta || null,
        remetente_jid: payload.remetente_jid,
        remetente_nome: payload.remetente_nome,
        external_id: payload.external_id,
        mensagem_em: payload.mensagem_em || new Date().toISOString(),
      });
      this.$nextTick(() => this.scrollChatParaFim());
    },

    onMensagemAtualizada(payload) {
      if (!payload || !payload.id) return;
      const idx = this.mensagens.findIndex((m) => m.id === payload.id);
      if (idx < 0) return;
      const atual = this.mensagens[idx];
      this.mensagens.splice(idx, 1, {
        ...atual,
        midia_url: payload.midia_url || atual.midia_url,
        midia_meta: payload.midia_meta || atual.midia_meta,
      });
    },

    onComposerEnviado() {
      this.$nextTick(() => this.scrollChatParaFim());
    },

    // ---- Enquete / Teste (quiz) ----------------------------------------
    enqCabecalho(e) {
      if (!e) return 'Enquete';
      if (e.quiz) return 'Teste · resposta única';
      return e.selecionaveis > 1 ? 'Enquete · múltipla escolha' : 'Enquete · escolha única';
    },
    // Leitura de resultado no card (mesmo shape dos grupos). Em canal os votos
    // podem não voltar; sem `resultado` as barras ficam em 0%, igual grupo novo.
    resultadoEnquete(item) {
      return (item && item.midia_meta && item.midia_meta.enquete && item.midia_meta.enquete.resultado) || null;
    },
    votosDaOpcao(item, i) {
      const r = this.resultadoEnquete(item);
      return (r && Array.isArray(r.porOpcao) && r.porOpcao[i]) || 0;
    },
    totalVotantesEnquete(item) {
      const r = this.resultadoEnquete(item);
      return (r && r.totalVotantes) || 0;
    },
    pctDaOpcao(item, i) {
      const total = this.totalVotantesEnquete(item);
      if (!total) return 0;
      return Math.round((this.votosDaOpcao(item, i) / total) * 100);
    },
    isQuizCorreta(item, i) {
      const e = item && item.midia_meta && item.midia_meta.enquete;
      return Boolean(e && e.quiz && e.correta === i);
    },
    abrirModalInterativo(quiz) {
      this.modalInterativo = {
        visivel: true, quiz: Boolean(quiz), pergunta: '', opcoes: ['', ''], multipla: false, correta: 0, enviando: false,
      };
      this.emojiPicker = { aberto: false, campo: null, i: -1 };
    },
    fecharModalInterativo() {
      if (this.modalInterativo.enviando) return;
      this.modalInterativo.visivel = false;
      this.emojiPicker = { aberto: false, campo: null, i: -1 };
    },
    // Abre/fecha o picker de emoji apontando para um campo (pergunta ou opção i).
    toggleEmojiModal(campo, i = -1) {
      const mesmo = this.emojiPicker.aberto && this.emojiPicker.campo === campo && this.emojiPicker.i === i;
      this.emojiPicker = { aberto: !mesmo, campo, i };
    },
    inserirEmojiModal(emoji) {
      const { campo, i } = this.emojiPicker;
      const pergunta = campo === 'pergunta';
      const limite = pergunta ? 255 : 100;
      const el = pergunta
        ? this.$refs.mPergunta
        : (Array.isArray(this.$refs.mOpcao) ? this.$refs.mOpcao[i] : this.$refs.mOpcao);
      const atual = pergunta ? this.modalInterativo.pergunta : (this.modalInterativo.opcoes[i] || '');
      if ((atual + emoji).length > limite) return; // respeita os limites do WhatsApp
      const pos = el && typeof el.selectionStart === 'number' ? el.selectionStart : atual.length;
      const novo = `${atual.substring(0, pos)}${emoji}${atual.substring(pos)}`;
      if (pergunta) this.modalInterativo.pergunta = novo;
      else this.modalInterativo.opcoes[i] = novo;
      this.emojiPicker.aberto = false;
      this.$nextTick(() => {
        if (el && el.focus) {
          el.focus();
          const np = pos + emoji.length;
          if (el.setSelectionRange) el.setSelectionRange(np, np);
        }
      });
    },
    adicionarOpcaoInterativo() {
      if (this.modalInterativo.opcoes.length >= 12) return;
      this.modalInterativo.opcoes.push('');
      this.emojiPicker.aberto = false;
    },
    removerOpcaoInterativo(i) {
      if (this.modalInterativo.opcoes.length <= 2) return;
      this.modalInterativo.opcoes.splice(i, 1);
      this.emojiPicker.aberto = false;
      // Mantém a resposta correta apontando pra opção certa após remover.
      if (this.modalInterativo.correta >= this.modalInterativo.opcoes.length) {
        this.modalInterativo.correta = this.modalInterativo.opcoes.length - 1;
      } else if (i < this.modalInterativo.correta) {
        this.modalInterativo.correta -= 1;
      }
    },
    async enviarInterativo() {
      if (!this.interativoValido || this.modalInterativo.enviando || !this.canalAtivoId) return;
      const quiz = this.modalInterativo.quiz;
      // Índice correto conta só nas opções preenchidas (o backend recebe as limpas).
      const opcoesLimpas = this.modalInterativo.opcoes.map((o) => (o || '').trim()).filter(Boolean);
      const corretaTexto = (this.modalInterativo.opcoes[this.modalInterativo.correta] || '').trim();
      const payload = {
        pergunta: this.modalInterativo.pergunta.trim(),
        opcoes: opcoesLimpas,
      };
      if (quiz) payload.correta = Math.max(0, opcoesLimpas.indexOf(corretaTexto));
      else payload.multipla = this.modalInterativo.multipla;

      this.modalInterativo.enviando = true;
      try {
        await axios.post(`/newsletter/${quiz ? 'teste' : 'enquete'}/${this.canalAtivoId}`, payload);
        this.modalInterativo.visivel = false;
        this.sucesso = quiz ? 'Teste publicado no canal.' : 'Enquete publicada no canal.';
        this.$nextTick(() => this.scrollChatParaFim());
      } catch (error) {
        this.erro = `Falha ao publicar: ${error.response?.data?.error || error.message}`;
      } finally {
        this.modalInterativo.enviando = false;
      }
    },

    async retryDownload(item) {
      if (!item || !item.id || this.retry[item.id]) return;
      this.retry = { ...this.retry, [item.id]: true };
      try {
        const { data } = await axios.post(`/newsletter/baixarMidia/${item.id}`);
        const idx = this.mensagens.findIndex((m) => m.id === item.id);
        if (idx >= 0) {
          const novo = { ...this.mensagens[idx], midia_url: data.midia_url, midia_meta: data.midia_meta };
          this.mensagens.splice(idx, 1, novo);
        }
      } catch (error) {
        this.erro = `Falha ao baixar: ${error.response?.data?.error || error.message}`;
      } finally {
        const rest = { ...this.retry };
        delete rest[item.id];
        this.retry = rest;
      }
    },

    abrirLightbox(tipo, midiaUrl, filename) {
      if (!midiaUrl) return;
      this.lightbox = { tipo, url: this.resolveAssetUrl(midiaUrl), filename };
      document.body.style.overflow = 'hidden';
    },
    fecharLightbox() {
      this.lightbox = null;
      document.body.style.overflow = '';
    },

    // === Painel info (edição rápida no painel info) ===
    abrirPainelInfo() {
      this.painelInfoAberto = true;
      if (this.canalAtivo) {
        this.novoNome = this.canalAtivo.nome || '';
        this.novaDescricao = this.canalAtivo.descricao || '';
      }
    },

    async salvarNome() {
      if (!this.canalAtivo) return;
      if (!this.novoNome.trim() || this.novoNome === this.canalAtivo.nome) {
        this.editandoNome = false;
        return;
      }
      try {
        const { data } = await axios.post(`/newsletter/renomear/${this.canalAtivoId}`, { nome: this.novoNome.trim() });
        const idx = this.canais.findIndex((x) => x.id === this.canalAtivoId);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], ...data });
        this.editandoNome = false;
        this.sucesso = 'Nome atualizado.';
      } catch (error) {
        this.erro = `Falha: ${error.response?.data?.error || error.message}`;
      }
    },

    async salvarDescricao() {
      if (!this.canalAtivo) return;
      try {
        const { data } = await axios.post(`/newsletter/descricao/${this.canalAtivoId}`, { descricao: this.novaDescricao });
        const idx = this.canais.findIndex((x) => x.id === this.canalAtivoId);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], ...data });
        this.editandoDescricao = false;
        this.sucesso = 'Descrição atualizada.';
      } catch (error) {
        this.erro = `Falha: ${error.response?.data?.error || error.message}`;
      }
    },

    copiarLink() {
      if (!this.linkCompartilhamentoAtivo) return;
      try {
        navigator.clipboard.writeText(this.linkCompartilhamentoAtivo);
        this.sucesso = 'Link copiado.';
      } catch (_e) {
        this.erro = 'Não foi possível copiar — copie manualmente.';
      }
    },

    onFotoSelecionada(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = ''; // permite reescolher o mesmo arquivo
      if (!file || !this.canalAtivoId) return;
      // Abre o recortador antes de enviar.
      this.cropFoto = { visivel: true, file, enviando: false, erro: null };
    },

    async onFotoRecortada(blob) {
      if (!blob || !this.canalAtivoId) return;
      this.cropFoto.enviando = true;
      this.cropFoto.erro = null;
      try {
        const fd = new FormData();
        fd.append('file', blob, 'canal.jpg');
        const { data } = await axios.post(`/newsletter/foto/${this.canalAtivoId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const idx = this.canais.findIndex((x) => x.id === this.canalAtivoId);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], ...data });
        this.sucesso = 'Foto do canal atualizada.';
        this.cropFoto = { visivel: false, file: null, enviando: false, erro: null };
      } catch (error) {
        // Mantém o modal aberto e mostra o motivo DENTRO dele.
        this.cropFoto.erro = `Falha ao atualizar foto: ${error.response?.data?.error || error.response?.data?.details || error.message}`;
      } finally {
        this.cropFoto.enviando = false;
      }
    },

    async sairCanal() {
      if (!this.canalAtivo) return;
      if (!window.confirm('Deixar de seguir este canal?')) return;
      try {
        await axios.post(`/newsletter/sair/${this.canalAtivoId}`);
        const idx = this.canais.findIndex((x) => x.id === this.canalAtivoId);
        if (idx >= 0) this.canais.splice(idx, 1, { ...this.canais[idx], arquivado: true });
        this.fecharChat();
        this.sucesso = 'Você deixou de seguir o canal.';
      } catch (error) {
        this.erro = `Falha: ${error.response?.data?.error || error.message}`;
      }
    },

    async removerCanal() {
      if (!this.canalAtivo) return;
      const msg = this.canalAtivo.is_dono
        ? 'Você é dono deste canal — REMOVER vai deletar o canal no WhatsApp. Continuar?'
        : 'Remover este canal da sua lista? (Você também vai deixar de seguir.)';
      if (!window.confirm(msg)) return;
      try {
        await axios.delete(`/newsletter/${this.canalAtivoId}`);
        const idx = this.canais.findIndex((x) => x.id === this.canalAtivoId);
        if (idx >= 0) this.canais.splice(idx, 1);
        this.fecharChat();
        this.sucesso = 'Canal removido.';
      } catch (error) {
        this.erro = `Falha: ${error.response?.data?.error || error.message}`;
      }
    },

    // === Recuperar canais do WhatsApp (via API) ===
    async sincronizarTodos() {
      // Sincroniza o canal Baileys selecionado no filtro, ou todos os conectados.
      const alvos = this.filtros.canal_baileys_id
        ? this.baileysConectados.filter((c) => Number(c.id) === Number(this.filtros.canal_baileys_id))
        : this.baileysConectados;
      if (!alvos.length) { this.erro = 'Nenhum canal Baileys conectado para sincronizar.'; return; }

      this.sincronizandoTodos = true;
      this.erro = null;
      let total = 0;
      try {
        for (const c of alvos) {
          try {
            const { data } = await axios.post(`/newsletter/sincronizarTodos/${c.id}`);
            total += (data && data.total) || 0;
          } catch (e) {
            this.erro = `Falha ao sincronizar "${c.nome}": ${e.response?.data?.error || e.message}`;
          }
        }
        await this.carregar();
        if (!this.erro) this.sucesso = `${total} canal(is) recuperado(s) do WhatsApp.`;
      } finally {
        this.sincronizandoTodos = false;
      }
    },

    // === Modais criar/importar ===
    abrirImportar() {
      this.importar = {
        canal_baileys_id: this.filtros.canal_baileys_id || (this.baileysConectados[0] && this.baileysConectados[0].id) || '',
        identificador: '',
        seguir: true,
      };
      this.erro = null;
      this.modalImportar = true;
    },

    async confirmarImportar() {
      if (!this.importar.canal_baileys_id) { this.erro = 'Escolha um canal API-B.'; return; }
      if (!this.importar.identificador.trim()) { this.erro = 'Cole o link ou JID do canal.'; return; }
      this.importando = true;
      try {
        await axios.post(`/newsletter/importar/${this.importar.canal_baileys_id}`, {
          identificador: this.importar.identificador.trim(),
          seguir: this.importar.seguir,
        });
        this.sucesso = 'Canal importado.';
        this.modalImportar = false;
        await this.carregar();
      } catch (error) {
        this.erro = `Falha ao importar: ${error.response?.data?.error || error.message}`;
      } finally {
        this.importando = false;
      }
    },

    abrirCriar() {
      this.novoCanal = {
        canal_baileys_id: this.filtros.canal_baileys_id || (this.baileysConectados[0] && this.baileysConectados[0].id) || '',
        nome: '',
        descricao: '',
      };
      this.erro = null;
      this.modalCriar = true;
    },

    async confirmarCriar() {
      if (!this.novoCanal.canal_baileys_id) { this.erro = 'Escolha um canal API-B.'; return; }
      if (!this.novoCanal.nome.trim()) { this.erro = 'Informe o nome do canal.'; return; }
      this.criando = true;
      try {
        await axios.post('/newsletter', {
          canal_baileys_id: Number(this.novoCanal.canal_baileys_id),
          nome: this.novoCanal.nome.trim(),
          descricao: this.novoCanal.descricao,
        });
        this.sucesso = 'Canal criado.';
        this.modalCriar = false;
        await this.carregar();
      } catch (error) {
        this.erro = `Falha ao criar canal: ${error.response?.data?.error || error.message}`;
      } finally {
        this.criando = false;
      }
    },

    // === Helpers ===
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^https?:\/\//i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    formatarTamanho(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
    // ── Helpers de card padronizados com os grupos ─────────────────────
    // Imagem do card de link: thumbnail salvo ou, se faltar, deriva do YouTube.
    linkThumb(preview) {
      if (!preview) return '';
      if (preview.thumbnail_url) return this.resolveAssetUrl(preview.thumbnail_url);
      return this.youtubeThumb(preview.canonical_url || preview.url || preview.matched_text);
    },
    youtubeThumb(url) {
      if (!url) return '';
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./i, '');
        let id = null;
        if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
        else if (/(^|\.)youtube\.com$/i.test(host)) {
          id = u.searchParams.get('v') || (u.pathname.match(/\/(?:shorts|embed|live)\/([^/?#]+)/) || [])[1];
        }
        if (id && /^[\w-]{6,}$/.test(id)) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
      } catch (_e) { /* url inválida */ }
      return '';
    },
    // Mini-mapa GRÁTIS com tiles do OpenStreetMap centralizados na coordenada.
    // Lê latitude/longitude do link_preview (mesmo shape dos grupos).
    mapaTiles(preview) {
      if (!preview || preview.latitude == null || preview.longitude == null) return [];
      const lat = Number(preview.latitude);
      const lng = Number(preview.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
      const z = 15;
      const TS = 256;
      const W = 330;
      const H = 150;
      const n = 2 ** z;
      const px = ((lng + 180) / 360) * n * TS;
      const latRad = (lat * Math.PI) / 180;
      const py = (0.5 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / (2 * Math.PI)) * n * TS;
      const originX = px - W / 2;
      const originY = py - H / 2;
      const tiles = [];
      for (let tx = Math.floor(originX / TS); tx <= Math.floor((originX + W) / TS); tx += 1) {
        for (let ty = Math.floor(originY / TS); ty <= Math.floor((originY + H) / TS); ty += 1) {
          if (ty < 0 || ty >= n) continue;
          const xt = ((tx % n) + n) % n;
          tiles.push({
            url: `https://tile.openstreetmap.org/${z}/${xt}/${ty}.png`,
            left: Math.round(tx * TS - originX),
            top: Math.round(ty * TS - originY),
          });
        }
      }
      return tiles;
    },
    // Hora sobre a MÍDIA/mapa (overlay escuro): imagem/vídeo sem legenda e mapa.
    metaSobrepostaDark(item) {
      if (item.tipo === 'localizacao' && item.midia_meta && item.midia_meta.link_preview) return true;
      if ((item.tipo === 'imagem' || item.tipo === 'video') && item.midia_url && !item.conteudo) return true;
      return false;
    },
    // Hora sobre card de corpo claro (overlay sutil): link, enquete e áudio.
    metaSub(item) {
      if (item.tipo === 'audio' && item.midia_url) return true;
      if (item.midia_meta && item.midia_meta.enquete) return true;
      if (item.tipo === 'texto' && item.midia_meta && item.midia_meta.link_preview) return true;
      return false;
    },
    // Qualquer overlay ativo → esconde a linha de meta separada de baixo.
    metaSobreposta(item) {
      return this.metaSobrepostaDark(item) || this.metaSub(item);
    },
    scrollChatParaFim() {
      const el = this.$refs.chatScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    previewMensagem(c) {
      const tipo = c.ultima_mensagem_tipo;
      const conteudo = c.ultima_mensagem_preview;
      if (conteudo) return conteudo;
      const labels = {
        imagem: '📷 Imagem', audio: '🎤 Áudio', video: '📹 Vídeo',
        documento: '📄 Documento', sticker: '😀 Sticker', localizacao: '📍 Localização',
      };
      return labels[tipo] || '';
    },
    autorPreview(c) {
      if (c.ultima_mensagem_direcao === 'saida') return 'Você: ';
      return '';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Canais" pageTitle="Canais do WhatsApp" />

    <BCard no-body class="canais-card">
      <div class="card-header flex-wrap gap-2 align-items-center d-none d-md-flex">
        <div class="flex-grow-1">
          <h5 class="m-0">Canais do WhatsApp (Newsletter / Broadcast)</h5>
          <small class="text-muted">Importe pelo link, crie um novo, e selecione à esquerda pra ver os posts.</small>
        </div>
        <button type="button" class="btn btn-soft-primary" :disabled="!baileysConectados.length || sincronizandoTodos" @click="sincronizarTodos" title="Recuperar do WhatsApp os canais que este número segue/administra">
          <i v-if="sincronizandoTodos" class="ri-loader-4-line spin me-1"></i>
          <i v-else class="ri-refresh-line me-1"></i>
          {{ sincronizandoTodos ? 'Sincronizando...' : 'Sincronizar' }}
        </button>
        <button type="button" class="btn btn-soft-secondary" :disabled="!baileysConectados.length" @click="abrirImportar">
          <i class="ri-link me-1"></i>Importar
        </button>
        <button type="button" class="btn btn-success" :disabled="!baileysConectados.length" @click="abrirCriar">
          <i class="ri-add-line me-1"></i>Criar canal
        </button>
      </div>

      <div v-if="erro" class="alert alert-danger m-2 mb-0">
        <i class="ri-error-warning-line me-1"></i>{{ erro }}
        <button class="btn-close float-end" @click="erro = null"></button>
      </div>
      <div v-if="sucesso" class="alert alert-success m-2 mb-0">
        <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
        <button class="btn-close float-end" @click="sucesso = null"></button>
      </div>

      <div class="wa-split">
        <!-- ============== Coluna esquerda: lista ============== -->
        <aside class="wa-sidebar" :class="{ 'd-none d-md-flex': canalAtivoId }">
          <div class="wa-sidebar-header">
            <div class="wa-sidebar-busca">
              <div class="d-none d-md-flex gap-2 align-items-center mb-2">
                <select v-model="filtros.canal_baileys_id" class="form-select form-select-sm flex-grow-1" @change="carregar">
                  <option value="">Todos os canais</option>
                  <option v-for="c in baileysConectados" :key="c.id" :value="c.id">{{ c.nome }}</option>
                </select>
              </div>
              <div class="wa-search-box">
                <i class="ri-search-line wa-search-icon"></i>
                <input v-model="filtros.busca" type="text" class="wa-search-input" placeholder="Pesquisar" />
              </div>
            </div>
            <div class="wa-sidebar-filtros">
              <div class="wa-chips">
                <button type="button" class="wa-chip" :class="{ active: !filtros.somenteAdmin }" @click="filtros.somenteAdmin = false">Todos</button>
                <button type="button" class="wa-chip" :class="{ active: filtros.somenteAdmin }" @click="filtros.somenteAdmin = true">
                  <i class="ri-shield-check-line"></i>Sou admin
                </button>
              </div>
            </div>
          </div>

          <div v-if="!baileysConectados.length" class="alert alert-warning m-2 small">
            <i class="ri-error-warning-line me-1"></i>
            Nenhum canal API-B conectado.
            <router-link to="/app/cadastros/canais-baileys">Conectar</router-link>
          </div>

          <div v-if="carregando" class="text-center text-muted py-4">
            <i class="ri-loader-4-line spin"></i>
          </div>
          <div v-else-if="!canaisFiltrados.length" class="text-center text-muted py-4 px-3 small">
            <i class="ri-broadcast-line" style="font-size: 1.8rem;"></i>
            <div class="mt-2">Nenhum canal. Importe um pelo link ou crie um novo.</div>
          </div>
          <ul v-else class="wa-conv-list">
            <li
              v-for="c in canaisFiltrados"
              :key="c.id"
              class="wa-conv-item"
              :class="{ 'wa-conv-item--ativa': c.id === canalAtivoId, 'wa-conv-item--nao-lida': c.nao_lidas_count > 0 }"
              @click="abrirChat(c)"
            >
              <div class="wa-conv-avatar wa-conv-avatar--canal">
                <img v-if="c.imagem_url" :src="c.imagem_url" alt="" />
                <i v-else class="ri-broadcast-line"></i>
              </div>
              <div class="wa-conv-body">
                <div class="d-flex justify-content-between align-items-baseline">
                  <strong class="text-truncate me-2">
                    {{ c.nome || '(sem nome)' }}
                    <i v-if="c.verificado" class="ri-verified-badge-fill text-info ms-1" title="Verificado"></i>
                  </strong>
                  <small class="wa-conv-hora flex-shrink-0" :class="{ 'text-success fw-semibold': c.nao_lidas_count > 0 }">{{ formatarHora(c.ultima_mensagem_em) }}</small>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <small class="wa-conv-preview text-truncate" :class="{ 'fw-semibold': c.nao_lidas_count > 0, 'text-muted': !c.nao_lidas_count }">
                    <template v-if="previewMensagem(c)">
                      <span class="text-muted">{{ autorPreview(c) }}</span>{{ previewMensagem(c) }}
                    </template>
                    <template v-else>
                      <i class="ri-user-3-line me-1"></i>{{ c.inscritos_count }} inscritos
                      <span v-if="!filtros.canal_baileys_id && c.canal && c.canal.nome"> · {{ c.canal.nome }}</span>
                    </template>
                  </small>
                  <div class="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                    <span
                      v-if="baileysDoCanal(c).length > 1"
                      class="badge bg-info-subtle text-info"
                      :title="resumoBaileysDoCanal(c)"
                    >
                      <i class="ri-whatsapp-line"></i> {{ baileysDoCanal(c).length }}
                    </span>
                    <span v-if="c.is_dono" class="badge bg-success-subtle text-success">dono</span>
                    <span v-else-if="c.is_admin" class="badge bg-primary-subtle text-primary">admin</span>
                    <span v-if="c.nao_lidas_count > 0" class="wa-badge-unread">{{ c.nao_lidas_count > 99 ? '99+' : c.nao_lidas_count }}</span>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </aside>

        <!-- ============== Coluna direita: chat ============== -->
        <section class="wa-chat" :class="{ 'd-none d-md-flex': !canalAtivoId }">
          <div v-if="!canalAtivoId" class="wa-empty">
            <i class="ri-broadcast-line" style="font-size: 4rem; opacity: 0.3;"></i>
            <p class="text-muted mt-3">Selecione um canal à esquerda para ver os posts.</p>
          </div>

          <template v-else-if="canalAtivo">
            <header class="wa-chat-header">
              <button type="button" class="btn btn-link p-0 wa-chat-hbtn d-md-none" title="Voltar" @click="fecharChat">
                <i class="ri-arrow-left-line"></i>
              </button>
              <div class="wa-chat-avatar wa-conv-avatar--canal" @click="abrirPainelInfo" style="cursor: pointer;">
                <img v-if="canalAtivo.imagem_url" :src="resolveAssetUrl(canalAtivo.imagem_url)" alt="" />
                <i v-else class="ri-broadcast-line"></i>
              </div>
              <div class="flex-grow-1 ms-2 text-truncate" @click="abrirPainelInfo" style="cursor: pointer;">
                <div class="fw-semibold text-truncate d-flex align-items-center gap-1">
                  {{ canalAtivo.nome || '(sem nome)' }}
                  <i v-if="canalAtivo.verificado" class="ri-verified-badge-fill text-info" title="Verificado"></i>
                </div>
                <small class="text-muted">
                  <i class="ri-user-3-line me-1"></i>{{ canalAtivo.inscritos_count }} inscritos
                  <span v-if="canalAtivo.is_dono" class="badge bg-success-subtle text-success ms-1">dono</span>
                  <span v-else-if="canalAtivo.is_admin" class="badge bg-primary-subtle text-primary ms-1">admin</span>
                  <span v-else class="badge bg-light text-muted ms-1">seguidor</span>
                </small>
              </div>
              <!-- Mesmo canal em mais de um WhatsApp: por qual número postar.
                   Só aparece quando há ambiguidade de verdade. -->
              <div v-if="ocorrenciasDoAtivo.length > 1" class="dropdown me-1 flex-shrink-0">
                <button
                  type="button"
                  class="btn btn-sm btn-soft-secondary"
                  data-bs-toggle="dropdown"
                  :title="`Postando por ${nomeDoBaileys(canalAtivo)} — clique para trocar`"
                >
                  <i class="ri-whatsapp-line"></i>
                  <span class="d-none d-sm-inline ms-1">{{ nomeDoBaileys(canalAtivo) }}</span>
                  <i class="ri-arrow-down-s-line ms-1"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><h6 class="dropdown-header">Postar por</h6></li>
                  <li v-for="o in ocorrenciasDoAtivo" :key="o.id">
                    <a
                      class="dropdown-item d-flex align-items-center gap-2"
                      :class="{ active: o.id === canalAtivoId }"
                      href="#"
                      @click.prevent="trocarCanalDoJid(o)"
                    >
                      <i :class="o.id === canalAtivoId ? 'ri-check-line' : 'ri-whatsapp-line'"></i>
                      <span class="flex-grow-1 text-truncate">{{ nomeDoBaileys(o) }}</span>
                      <span v-if="o.is_dono" class="badge bg-success-subtle text-success">dono</span>
                      <span v-else-if="o.is_admin" class="badge bg-primary-subtle text-primary">admin</span>
                    </a>
                  </li>
                </ul>
              </div>

              <div class="dropdown">
                <button type="button" class="btn btn-link p-0 wa-chat-hbtn" data-bs-toggle="dropdown" title="Mais opções">
                  <i class="ri-more-2-fill"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#" @click.prevent="abrirPainelInfo"><i class="ri-information-line me-1"></i>Info do canal</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="carregarMensagens"><i class="ri-restart-line me-1"></i>Recarregar posts</a></li>
                  <li><a class="dropdown-item" :class="{ disabled: sincronizandoAtivo }" href="#" @click.prevent="sincronizarAtivo">
                    <i v-if="sincronizandoAtivo" class="ri-loader-4-line spin me-1"></i>
                    <i v-else class="ri-refresh-line me-1"></i>Sincronizar
                  </a></li>
                </ul>
              </div>
            </header>

            <div ref="chatScroll" class="wa-chat-messages">
              <div v-if="carregandoMensagens" class="text-center text-muted py-4">
                <i class="ri-loader-4-line spin"></i>
              </div>
              <div v-else-if="!mensagens.length" class="text-center text-muted py-5 small">
                <template v-if="isAdminAtivo">
                  Nenhum post ainda. Use o campo abaixo para publicar.
                </template>
                <template v-else>
                  Nenhum post foi capturado ainda. Posts novos aparecem aqui em tempo real.
                </template>
              </div>
              <template v-else>
                <template v-for="item in mensagensComSeparador" :key="item.id">
                  <div v-if="item.separador" class="wa-data-sep">
                    <span>{{ item.label }}</span>
                  </div>
                  <div v-else class="wa-msg-row" :class="{ 'wa-msg-row--out': item.direcao === 'saida' }">
                    <div
                      class="wa-msg-bubble"
                      :class="{
                        'wa-msg-bubble--out': item.direcao === 'saida',
                        'wa-msg-bubble--midia': item.midia_url && ['imagem','video','sticker'].includes(item.tipo),
                        'wa-msg-bubble--card': item.midia_meta && (item.midia_meta.link_preview || item.midia_meta.enquete),
                        'wa-msg-bubble--metasub': metaSub(item),
                      }"
                    >
                      <!-- Imagem -->
                      <div v-if="item.tipo === 'imagem'" class="wa-msg-media">
                        <a v-if="item.midia_url" href="#" class="wa-msg-img-wrap" @click.prevent="abrirLightbox('imagem', item.midia_url)">
                          <img :src="resolveAssetUrl(item.midia_url)" class="wa-msg-img" alt="" />
                          <span class="wa-msg-img-zoom"><i class="ri-fullscreen-line"></i></span>
                          <span v-if="metaSobreposta(item)" class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </a>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-image-line"></i>
                          <span>Imagem indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>
                      <!-- Sticker -->
                      <div v-else-if="item.tipo === 'sticker'" class="wa-msg-media">
                        <img v-if="item.midia_url" :src="resolveAssetUrl(item.midia_url)" class="wa-msg-sticker" alt="" />
                        <div v-else class="wa-msg-tipo text-muted small"><i class="ri-emotion-line me-1"></i>Sticker</div>
                      </div>
                      <!-- Vídeo (preview + botão de expandir) -->
                      <div v-else-if="item.tipo === 'video'" class="wa-msg-media wa-msg-video-wrap">
                        <template v-if="item.midia_url">
                          <video :src="resolveAssetUrl(item.midia_url)" preload="metadata" class="wa-msg-video"></video>
                          <button type="button" class="wa-msg-video-play" @click="abrirLightbox('video', item.midia_url)" title="Assistir em tela cheia">
                            <i class="ri-play-fill"></i>
                          </button>
                          <span v-if="metaSobreposta(item)" class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </template>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-video-line"></i>
                          <span>Vídeo indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>
                      <!-- Áudio -->
                      <div v-else-if="item.tipo === 'audio'" class="wa-msg-audio-wrap">
                        <AudioPlayer v-if="item.midia_url" :src="resolveAssetUrl(item.midia_url)" :invertido="item.direcao === 'saida'" />
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-mic-line"></i>
                          <span>Áudio indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>
                      <!-- Documento -->
                      <div v-else-if="item.tipo === 'documento'" class="wa-msg-doc">
                        <a v-if="item.midia_url" :href="resolveAssetUrl(item.midia_url)" target="_blank" class="d-flex align-items-center gap-2 text-decoration-none">
                          <i class="ri-file-line wa-msg-doc-icon"></i>
                          <div class="flex-grow-1 min-w-0">
                            <div class="text-truncate fw-semibold">{{ (item.midia_meta && item.midia_meta.filename) || 'documento' }}</div>
                            <small class="text-muted">{{ formatarTamanho(item.midia_meta && item.midia_meta.size) }}</small>
                          </div>
                          <i class="ri-download-line"></i>
                        </a>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-file-line"></i>
                          <span>Documento indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>
                      <!-- Localização: card com mapa real (tiles OSM), igual aos grupos -->
                      <a
                        v-if="item.tipo === 'localizacao' && item.midia_meta && item.midia_meta.link_preview"
                        :href="item.midia_meta.link_preview.url"
                        target="_blank"
                        class="wa-link-preview wa-loc-preview"
                      >
                        <div class="wa-loc-map">
                          <img
                            v-for="(t, i) in mapaTiles(item.midia_meta.link_preview)"
                            :key="i"
                            :src="t.url"
                            class="wa-loc-tile"
                            :style="{ left: t.left + 'px', top: t.top + 'px' }"
                            alt=""
                            loading="lazy"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <i class="ri-map-pin-2-fill wa-loc-pin"></i>
                          <span class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </div>
                        <div v-if="item.midia_meta.link_preview.title || item.midia_meta.link_preview.description" class="wa-link-body">
                          <div v-if="item.midia_meta.link_preview.title" class="wa-link-title text-truncate">{{ item.midia_meta.link_preview.title }}</div>
                          <div v-if="item.midia_meta.link_preview.description" class="wa-link-desc text-truncate">{{ item.midia_meta.link_preview.description }}</div>
                          <div class="wa-link-url text-truncate">Abrir no Google Maps</div>
                        </div>
                      </a>
                      <!-- Link preview (texto com URL detectada) -->
                      <a
                        v-else-if="item.tipo === 'texto' && item.midia_meta && item.midia_meta.link_preview"
                        :href="item.midia_meta.link_preview.url || item.midia_meta.link_preview.canonical_url"
                        target="_blank"
                        class="wa-link-preview"
                      >
                        <img v-if="linkThumb(item.midia_meta.link_preview)" :src="linkThumb(item.midia_meta.link_preview)" class="wa-link-thumb" alt="" @error="$event.target.remove()" />
                        <div class="wa-link-body">
                          <div v-if="item.midia_meta.link_preview.title" class="wa-link-title text-truncate">{{ item.midia_meta.link_preview.title }}</div>
                          <div v-if="item.midia_meta.link_preview.description" class="wa-link-desc text-truncate">{{ item.midia_meta.link_preview.description }}</div>
                          <div class="wa-link-url text-truncate">{{ item.midia_meta.link_preview.canonical_url || item.midia_meta.link_preview.url }}</div>
                        </div>
                      </a>
                      <!-- Enquete / Teste (quiz) — layout padronizado com os grupos -->
                      <div v-if="item.midia_meta && item.midia_meta.enquete" class="wa-enquete">
                        <div class="wa-enquete-cab">
                          <i :class="item.midia_meta.enquete.quiz ? 'ri-trophy-line' : 'ri-bar-chart-horizontal-line'" class="me-1"></i>
                          {{ enqCabecalho(item.midia_meta.enquete) }}
                        </div>
                        <div class="wa-enquete-pergunta">{{ item.midia_meta.enquete.pergunta }}</div>
                        <ul class="wa-enquete-opcoes">
                          <li v-for="(op, i) in item.midia_meta.enquete.opcoes" :key="i">
                            <div class="wa-enquete-op-linha">
                              <span>
                                {{ op }}
                                <i v-if="isQuizCorreta(item, i)" class="ri-check-line wa-enquete-correta" title="Resposta correta"></i>
                              </span>
                              <span class="wa-enquete-op-num">{{ votosDaOpcao(item, i) }}</span>
                            </div>
                            <div class="wa-enquete-barra">
                              <div class="wa-enquete-barra-fill" :style="{ width: pctDaOpcao(item, i) + '%' }"></div>
                            </div>
                          </li>
                        </ul>
                        <div class="wa-enquete-rodape">
                          <span>{{ totalVotantesEnquete(item) }} {{ totalVotantesEnquete(item) === 1 ? 'pessoa votou' : 'pessoas votaram' }}</span>
                        </div>
                      </div>

                      <!-- Caption / texto -->
                      <div v-if="item.conteudo && (item.tipo === 'imagem' || item.tipo === 'video' || item.tipo === 'documento' || item.tipo === 'texto')" class="wa-msg-conteudo" v-html="linkify(item.conteudo)"></div>

                      <div v-if="!metaSobreposta(item)" class="wa-msg-meta">
                        <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                        <i v-if="item.direcao === 'saida'" class="ri-check-double-line ms-1"></i>
                      </div>
                      <!-- Overlay de hora p/ link/enquete/áudio (reduz altura, igual grupos) -->
                      <div v-if="metaSub(item)" class="wa-meta-overlay--sub">
                        <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                        <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>

            <!-- Composer (admin) ou placeholder (seguidor) -->
            <CanalComposer
              :newsletter-id="canalAtivoId"
              :is-admin="isAdminAtivo"
              @enviado="onComposerEnviado"
              @erro="(msg) => (erro = msg)"
              @abrir-enquete="abrirModalInterativo(false)"
              @abrir-teste="abrirModalInterativo(true)"
            />
          </template>
        </section>

        <!-- ============== Painel info ============== -->
        <transition name="slide">
          <aside v-if="painelInfoAberto && canalAtivo" class="wa-info-panel">
            <header class="wa-info-header">
              <h6 class="m-0">Info do canal</h6>
              <button type="button" class="btn-close" @click="painelInfoAberto = false"></button>
            </header>
            <div class="wa-info-body">
              <div class="text-center py-3">
                <div class="wa-info-avatar mx-auto position-relative">
                  <img v-if="canalAtivo.imagem_url" :src="resolveAssetUrl(canalAtivo.imagem_url)" class="rounded-circle img-fluid" alt="" />
                  <i v-else class="ri-broadcast-line"></i>
                  <button
                    v-if="isAdminAtivo"
                    type="button"
                    class="wa-foto-btn"
                    :disabled="enviandoFoto"
                    title="Trocar foto do canal"
                    @click="$refs.fotoInput.click()"
                  >
                    <i v-if="enviandoFoto" class="ri-loader-4-line spin"></i>
                    <i v-else class="ri-camera-line"></i>
                  </button>
                </div>
                <input ref="fotoInput" type="file" accept="image/*" class="d-none" @change="onFotoSelecionada" />
                <div v-if="!editandoNome" class="mt-2 d-flex align-items-center justify-content-center gap-1">
                  <h5 class="m-0">{{ canalAtivo.nome || '(sem nome)' }}</h5>
                  <i v-if="canalAtivo.verificado" class="ri-verified-badge-fill text-info" title="Verificado"></i>
                  <button v-if="isAdminAtivo" type="button" class="btn btn-sm btn-link text-muted p-0 ms-1" @click="editandoNome = true">
                    <i class="ri-pencil-line"></i>
                  </button>
                </div>
                <div v-else class="mt-2 d-flex gap-2 align-items-center justify-content-center">
                  <input v-model="novoNome" class="form-control form-control-sm" maxlength="100" style="max-width: 220px;" @keyup.enter="salvarNome" />
                  <button class="btn btn-sm btn-success" @click="salvarNome"><i class="ri-check-line"></i></button>
                  <button class="btn btn-sm btn-light" @click="editandoNome = false"><i class="ri-close-line"></i></button>
                </div>
                <small class="text-muted d-block mt-1">{{ canalAtivo.inscritos_count }} inscritos</small>
              </div>

              <div class="border-top pt-3 mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <strong class="small">Descrição</strong>
                  <button v-if="isAdminAtivo && !editandoDescricao" class="btn btn-sm btn-link p-0" @click="editandoDescricao = true">
                    <i class="ri-pencil-line"></i>
                  </button>
                </div>
                <div v-if="!editandoDescricao" class="small text-muted mt-1">
                  {{ canalAtivo.descricao || 'Sem descrição.' }}
                </div>
                <div v-else class="mt-1">
                  <textarea v-model="novaDescricao" rows="3" class="form-control form-control-sm"></textarea>
                  <div class="mt-2 d-flex gap-2 justify-content-end">
                    <button class="btn btn-sm btn-light" @click="editandoDescricao = false">Cancelar</button>
                    <button class="btn btn-sm btn-success" @click="salvarDescricao">Salvar</button>
                  </div>
                </div>
              </div>

              <div v-if="linkCompartilhamentoAtivo" class="border-top pt-3 mb-3">
                <strong class="small d-block mb-1">Link de convite</strong>
                <div class="d-flex gap-2">
                  <input :value="linkCompartilhamentoAtivo" class="form-control form-control-sm" readonly />
                  <button type="button" class="btn btn-sm btn-soft-secondary" @click="copiarLink">
                    <i class="ri-clipboard-line"></i>
                  </button>
                </div>
              </div>

              <div class="border-top pt-3 mb-3">
                <strong class="small d-block mb-1">JID</strong>
                <code class="small text-break d-block">{{ canalAtivo.jid }}</code>
              </div>

              <div class="border-top pt-3 d-flex flex-column gap-2">
                <button v-if="!canalAtivo.is_admin" type="button" class="btn btn-sm btn-outline-warning" @click="sairCanal">
                  <i class="ri-eye-off-line me-1"></i>Deixar de seguir
                </button>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="removerCanal">
                  <i class="ri-delete-bin-line me-1"></i>
                  {{ canalAtivo.is_dono ? 'Deletar canal no WhatsApp' : 'Remover da lista' }}
                </button>
              </div>
            </div>
          </aside>
        </transition>
      </div>

      <!-- FAB "Criar canal": só no mobile e apenas na lista (esconde no chat aberto) -->
      <button
        v-if="!canalAtivoId"
        type="button"
        class="canais-fab d-md-none"
        :disabled="!baileysConectados.length"
        title="Criar canal"
        @click="abrirCriar"
      >
        <i class="ri-add-line"></i>
      </button>
    </BCard>

    <!-- Recorte da foto do canal antes do upload -->
    <CropFotoModal
      :visivel="cropFoto.visivel"
      :file="cropFoto.file"
      :enviando="cropFoto.enviando"
      :erro-externo="cropFoto.erro"
      @fechar="cropFoto = { visivel: false, file: null, enviando: false, erro: null }"
      @confirmar="onFotoRecortada"
    />

    <!-- Modal: importar -->
    <div v-if="modalImportar" class="modal-can-overlay" @click.self="modalImportar = false">
      <div class="modal-can-content">
        <div class="modal-can-header">
          <h5 class="m-0">Importar canal</h5>
          <button class="btn-close" @click="modalImportar = false"></button>
        </div>
        <div class="modal-can-body">
          <div class="mb-3">
            <label class="form-label">Canal API-B (número conectado)</label>
            <select v-model="importar.canal_baileys_id" class="form-select">
              <option value="">Selecione...</option>
              <option v-for="c in baileysConectados" :key="c.id" :value="c.id">{{ c.nome }} ({{ c.telefone }})</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Link de convite ou JID</label>
            <input v-model="importar.identificador" class="form-control" placeholder="https://whatsapp.com/channel/AbCdEf... OU 123...@newsletter" />
            <small class="text-muted">Cole o link público do canal ou o JID, se já souber.</small>
          </div>
          <div class="form-check">
            <input v-model="importar.seguir" class="form-check-input" type="checkbox" id="chk-seguir" />
            <label class="form-check-label small" for="chk-seguir">Seguir após importar (se não for admin)</label>
          </div>
        </div>
        <div class="modal-can-footer">
          <button type="button" class="btn btn-light" @click="modalImportar = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="importando" @click="confirmarImportar">
            <i v-if="importando" class="ri-loader-4-line spin me-1"></i>
            {{ importando ? 'Importando...' : 'Importar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: criar -->
    <div v-if="modalCriar" class="modal-can-overlay" @click.self="modalCriar = false">
      <div class="modal-can-content">
        <div class="modal-can-header">
          <h5 class="m-0">Criar canal</h5>
          <button class="btn-close" @click="modalCriar = false"></button>
        </div>
        <div class="modal-can-body">
          <div class="mb-3">
            <label class="form-label">Canal API-B (número conectado)</label>
            <select v-model="novoCanal.canal_baileys_id" class="form-select">
              <option value="">Selecione...</option>
              <option v-for="c in baileysConectados" :key="c.id" :value="c.id">{{ c.nome }} ({{ c.telefone }})</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Nome do canal</label>
            <input v-model="novoCanal.nome" type="text" class="form-control" maxlength="100" placeholder="Ex: Canal de Notícias" />
          </div>
          <div class="mb-2">
            <label class="form-label">Descrição (opcional)</label>
            <textarea v-model="novoCanal.descricao" rows="3" class="form-control"></textarea>
          </div>
        </div>
        <div class="modal-can-footer">
          <button type="button" class="btn btn-light" @click="modalCriar = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="criando" @click="confirmarCriar">
            <i v-if="criando" class="ri-loader-4-line spin me-1"></i>
            {{ criando ? 'Criando...' : 'Criar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Enquete / Teste (quiz) -->
    <div v-if="modalInterativo.visivel" class="modal-can-overlay" @click.self="fecharModalInterativo">
      <div class="modal-can-content">
        <div class="modal-can-header">
          <h5 class="m-0">
            <i :class="modalInterativo.quiz ? 'ri-trophy-line' : 'ri-bar-chart-horizontal-line'" class="me-1"></i>
            {{ modalInterativo.quiz ? 'Criar teste' : 'Criar enquete' }}
          </h5>
          <button class="btn-close" :disabled="modalInterativo.enviando" @click="fecharModalInterativo"></button>
        </div>
        <div class="modal-can-body">
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label m-0">Pergunta</label>
              <button type="button" class="modal-emoji-btn" title="Inserir emoji" :disabled="modalInterativo.enviando" @click="toggleEmojiModal('pergunta')">
                <i class="ri-emotion-happy-line"></i>
              </button>
            </div>
            <textarea
              ref="mPergunta"
              v-model="modalInterativo.pergunta"
              class="form-control mt-1"
              rows="2"
              maxlength="255"
              :placeholder="modalInterativo.quiz ? 'Ex: Qual a capital do Brasil?' : 'Sobre o que é a enquete?'"
              :disabled="modalInterativo.enviando"
            ></textarea>
            <div v-if="emojiPicker.aberto && emojiPicker.campo === 'pergunta'" class="modal-emoji-pop">
              <button v-for="(e, k) in emojis" :key="k" type="button" class="modal-emoji-item" @click="inserirEmojiModal(e)">{{ e }}</button>
            </div>
            <small class="text-muted">{{ (modalInterativo.pergunta || '').length }}/255</small>
          </div>

          <label class="form-label">Opções</label>
          <p v-if="modalInterativo.quiz" class="text-muted small mb-2">
            <i class="ri-checkbox-circle-line me-1"></i>Marque a resposta correta.
          </p>
          <div v-for="(op, i) in modalInterativo.opcoes" :key="i" class="mb-2">
            <div class="d-flex gap-2 align-items-center">
              <div v-if="modalInterativo.quiz" class="form-check m-0" :title="'Marcar como correta'">
                <input
                  v-model.number="modalInterativo.correta"
                  class="form-check-input mt-0"
                  type="radio"
                  :value="i"
                  :disabled="modalInterativo.enviando || !(modalInterativo.opcoes[i] || '').trim()"
                />
              </div>
              <input
                ref="mOpcao"
                v-model="modalInterativo.opcoes[i]"
                type="text"
                class="form-control"
                maxlength="100"
                :placeholder="`Opção ${i + 1}`"
                :disabled="modalInterativo.enviando"
                @keydown.enter.prevent
              />
              <button
                type="button"
                class="modal-emoji-btn"
                :disabled="modalInterativo.enviando"
                title="Inserir emoji"
                @click="toggleEmojiModal('opcao', i)"
              >
                <i class="ri-emotion-happy-line"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                :disabled="modalInterativo.opcoes.length <= 2 || modalInterativo.enviando"
                title="Remover opção"
                @click="removerOpcaoInterativo(i)"
              >
                <i class="ri-close-line"></i>
              </button>
            </div>
            <div v-if="emojiPicker.aberto && emojiPicker.campo === 'opcao' && emojiPicker.i === i" class="modal-emoji-pop">
              <button v-for="(e, k) in emojis" :key="k" type="button" class="modal-emoji-item" @click="inserirEmojiModal(e)">{{ e }}</button>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-soft-secondary"
            :disabled="modalInterativo.opcoes.length >= 12 || modalInterativo.enviando"
            @click="adicionarOpcaoInterativo"
          >
            <i class="ri-add-line me-1"></i>Adicionar opção
            <span class="text-muted ms-1">({{ modalInterativo.opcoes.length }}/12)</span>
          </button>

          <div v-if="!modalInterativo.quiz" class="form-check mt-3">
            <input
              id="interativoMultipla"
              v-model="modalInterativo.multipla"
              class="form-check-input"
              type="checkbox"
              :disabled="modalInterativo.enviando"
            />
            <label class="form-check-label" for="interativoMultipla">
              Permitir múltiplas respostas
            </label>
          </div>
        </div>
        <div class="modal-can-footer">
          <button type="button" class="btn btn-light" :disabled="modalInterativo.enviando" @click="fecharModalInterativo">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="!interativoValido || modalInterativo.enviando" @click="enviarInterativo">
            <i v-if="modalInterativo.enviando" class="ri-loader-4-line spin me-1"></i>
            {{ modalInterativo.enviando ? 'Publicando...' : (modalInterativo.quiz ? 'Publicar teste' : 'Publicar enquete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lightbox -->
    <div v-if="lightbox" class="wa-lightbox" @click.self="fecharLightbox">
      <button type="button" class="wa-lightbox-fechar" @click="fecharLightbox" title="Fechar (Esc)">
        <i class="ri-close-line"></i>
      </button>
      <a
        v-if="lightbox.tipo === 'imagem'"
        :href="lightbox.url"
        :download="lightbox.filename || true"
        class="wa-lightbox-baixar"
        title="Baixar"
        @click.stop
      >
        <i class="ri-download-line"></i>
      </a>
      <img v-if="lightbox.tipo === 'imagem'" :src="lightbox.url" class="wa-lightbox-img" alt="" />
      <video
        v-else-if="lightbox.tipo === 'video'"
        :src="lightbox.url"
        class="wa-lightbox-video"
        controls
        autoplay
        controlsList="nodownload"
      ></video>
    </div>
  </Layout>
</template>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.canais-card {
  display: flex; flex-direction: column;
  flex: 1; min-height: 0; overflow: hidden;
  margin-bottom: 0;
}

.wa-split {
  display: flex; flex: 1;
  min-height: 0; overflow: hidden;
  background: #f0f2f5;
  border-radius: 0 0 0.375rem 0.375rem;
  position: relative;
}
[data-bs-theme='dark'] .wa-split { background: #0b141a; }

.wa-sidebar {
  --wa-bg: #fff;
  width: 340px; flex-shrink: 0;
  display: flex; flex-direction: column;
  background: var(--vz-card-bg, #fff);
  border-right: 1px solid var(--vz-border-color);
  overflow: hidden;
}
[data-bs-theme='dark'] .wa-sidebar { --wa-bg: #111b21; }
/* Mobile: lista ocupa 100% da largura, card/painel full-bleed. */
@media (max-width: 767.98px) {
  .wa-sidebar { width: 100%; border-right: 0; }
  .wa-split { border-radius: 0; }
  .canais-card { border-radius: 0; border-left: 0; border-right: 0; }
  .wa-info-panel { width: 100%; }
}
.wa-sidebar-header {
  background: var(--wa-bg);
}
/* Banda da busca: borda logo abaixo do campo (igual Conversas). */
.wa-sidebar-busca {
  padding: 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
}
/* Banda dos filtros/chips: sem borda até a lista. */
.wa-sidebar-filtros {
  padding: 0.75rem;
}

/* Input de busca padronizado (igual ao de Conversas/Atendimento). */
.wa-search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 35px;
  padding: 0 12px;
  border-radius: 8px;
  background: #f0f2f5;
}
.wa-search-icon { color: #54656f; font-size: 16px; flex-shrink: 0; }
.wa-search-input {
  flex: 1; min-width: 0;
  border: 0; background: transparent; outline: none;
  font-size: 13px; color: #111b21; height: 100%;
}
.wa-search-input::placeholder { color: #667781; }
[data-bs-theme='dark'] .wa-search-box { background: #202c33; }
[data-bs-theme='dark'] .wa-search-icon { color: #aebac1; }
[data-bs-theme='dark'] .wa-search-input { color: #e9edef; }
[data-bs-theme='dark'] .wa-search-input::placeholder { color: #8696a0; }

/* Chips de filtro (mesmo estilo de grupos/atendimento). */
.wa-chips {
  display: flex; gap: 8px; overflow-x: auto; flex-wrap: nowrap;
  scrollbar-width: none;            /* Firefox: sem barra */
  -ms-overflow-style: none;         /* Edge legado: sem barra */
  -webkit-overflow-scrolling: touch; /* swipe com inércia (iOS) */
  overscroll-behavior-x: contain;
}
.wa-chips::-webkit-scrollbar { display: none; width: 0; height: 0; }
.wa-chip {
  border: none;
  border-radius: 18px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  background: #e9edef;
  color: #667781;
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.wa-chip:hover { background: #d1d7db; }
.wa-chip.active { background: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.15); color: var(--brand-primary, #971219); }
[data-bs-theme='dark'] .wa-chip { background: #202c33; color: #8696a0; }
[data-bs-theme='dark'] .wa-chip:hover { background: #374045; }
[data-bs-theme='dark'] .wa-chip.active { background: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.2); color: var(--brand-primary, #971219); }

.wa-conv-list {
  list-style: none; margin: 0; padding: 0;
  overflow-y: auto; flex: 1;
}
.wa-conv-item {
  display: flex; gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
  cursor: pointer; transition: background 0.1s;
  align-items: center;
}
.wa-conv-item:hover { background: var(--vz-light, #f5f6f7); }
[data-bs-theme='dark'] .wa-conv-item:hover { background: rgba(255,255,255,0.04); }
.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, #e7f1ff); }
[data-bs-theme='dark'] .wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, rgba(151, 18, 25, 0.16)); }

.wa-conv-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: var(--vz-primary-bg-subtle, #e9ecef);
  color: var(--vz-primary, #0d6efd);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 1.3rem; overflow: hidden;
}
.wa-conv-avatar img { width: 100%; height: 100%; object-fit: cover; }
.wa-conv-avatar--canal {
  background: linear-gradient(135deg, #128c7e, #25d366) !important;
  color: #fff !important;
}
.wa-conv-body { flex: 1; min-width: 0; }
.wa-conv-preview { max-width: 100%; }
.wa-conv-hora { color: var(--vz-text-muted, #878a99); font-size: 0.72rem; }
.wa-conv-item--nao-lida { background: rgba(0, 150, 100, 0.04); }
[data-bs-theme='dark'] .wa-conv-item--nao-lida { background: rgba(11, 178, 124, 0.08); }
.wa-conv-item--nao-lida.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, #e7f1ff); }
[data-bs-theme='dark'] .wa-conv-item--nao-lida.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, rgba(151, 18, 25, 0.16)); }

.wa-badge-unread {
  display: inline-flex;
  min-width: 20px; height: 20px;
  padding: 0 6px; border-radius: 10px;
  background: #25d366; color: #fff;
  font-size: 0.72rem; font-weight: 600;
  align-items: center; justify-content: center;
  line-height: 1;
}

.wa-chat {
  flex: 1;
  display: flex; flex-direction: column;
  background: var(--vz-card-bg, #fff);
  overflow: hidden; min-width: 0;
}
.wa-empty {
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 2rem;
}

.wa-chat-header {
  display: flex; align-items: center; gap: 0.25rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-light, #f8f9fa);
  flex-shrink: 0;
}
[data-bs-theme='dark'] .wa-chat-header { background: var(--vz-light, #1f2937); }
/* Botões de voltar e 3 pontos: mesmo tamanho do 3-pontos da topbar (42px, fs-22). */
.wa-chat-header .wa-chat-hbtn {
  width: 42px; height: 42px;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.375rem; line-height: 1;
  color: var(--vz-secondary-color);
}

/* Mobile: header da conversa com as cores da topbar (vermelho PCdoB),
   adaptando o amarelo ao modo escuro igual à topbar. */
@media (max-width: 767.98px) {
  .wa-chat-header {
    background: var(--vz-primary, #971219);
    border-bottom-color: rgba(0, 0, 0, 0.18);
  }
  [data-bs-theme='dark'] .wa-chat-header {
    background: var(--brand-primary-dark, #3f060a);
    border-bottom-color: rgba(0, 0, 0, 0.35);
  }
  .wa-chat-header .fw-semibold,
  .wa-chat-header .text-muted,
  .wa-chat-header .btn-link,
  .wa-chat-header i {
    color: var(--vz-yellow, #fff100) !important;
  }
  [data-bs-theme='dark'] .wa-chat-header .fw-semibold,
  [data-bs-theme='dark'] .wa-chat-header .text-muted,
  [data-bs-theme='dark'] .wa-chat-header .btn-link,
  [data-bs-theme='dark'] .wa-chat-header i {
    color: var(--vz-yellow, #f2c94c) !important;
  }
  /* O ícone do avatar do canal fica sobre gradiente verde — mantém branco. */
  .wa-chat-header .wa-chat-avatar i { color: #fff !important; }
}
.wa-chat-avatar {
  width: 40px; height: 40px;
  border-radius: 50%;
  background: var(--vz-primary-bg-subtle, #e9ecef);
  color: var(--vz-primary, #0d6efd);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
}
.wa-chat-avatar img { width: 100%; height: 100%; object-fit: cover; }

.wa-chat-messages {
  flex: 1; overflow-y: auto; padding: 1rem;
  /* Mesmo fundo do Atendimento (doodles PCdoB em SVG). */
  background-color: #fcf8f4;
  background-image: url('~@/assets/img/chat-bg-light.svg');
  background-repeat: repeat;
  background-size: 280px 280px;
  background-attachment: local;
}
[data-bs-theme='dark'] .wa-chat-messages {
  background-color: #03060a;
  background-image: url('~@/assets/img/chat-bg-dark.svg');
}

.wa-data-sep { display: flex; justify-content: center; margin: 0.75rem 0; }
.wa-data-sep span {
  background: rgba(255, 255, 255, 0.85);
  color: #555; font-size: 0.75rem;
  padding: 0.25rem 0.75rem; border-radius: 12px;
  text-transform: capitalize;
}
[data-bs-theme='dark'] .wa-data-sep span { background: rgba(255, 255, 255, 0.06); color: #ccc; }

.wa-msg-row { display: flex; margin-bottom: 0.4rem; }
.wa-msg-row--out { justify-content: flex-end; }

.wa-msg-bubble {
  max-width: 70%; background: #fff;
  padding: 0.45rem 0.7rem 0.3rem;
  border-radius: 8px;
  box-shadow: 0 1px 0.5px rgba(0,0,0,0.13);
  position: relative; word-wrap: break-word;
}
/* Balão da MINHA publicação: tom claro da primária (segue o whitelabel via
   --vz-primary; fallback fixo pra navegador sem color-mix) — igual aos grupos. */
.wa-msg-bubble--out {
  background: #f6e1e2;
  background: color-mix(in srgb, var(--vz-primary, #971219) 13%, #fff);
}
[data-bs-theme='dark'] .wa-msg-bubble { background: #202c33; color: #e9edef; box-shadow: 0 1px 0.5px rgba(0,0,0,0.4); }
[data-bs-theme='dark'] .wa-msg-bubble--out {
  background: #3d2529;
  background: color-mix(in srgb, var(--vz-primary, #971219) 42%, #1b2429);
  color: #e9edef;
}

.wa-msg-conteudo { font-size: 0.92rem; white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }
.wa-msg-conteudo :deep(a) { color: #027eb5; text-decoration: underline; overflow-wrap: anywhere; word-break: break-all; }
.wa-msg-bubble--out .wa-msg-conteudo :deep(a) { color: var(--vz-primary, #971219); }
[data-bs-theme='dark'] .wa-msg-conteudo :deep(a) { color: #53bdeb; }
[data-bs-theme='dark'] .wa-msg-bubble--out .wa-msg-conteudo :deep(a) { color: #f0aeb4; }
.wa-msg-meta { text-align: right; font-size: 0.7rem; color: rgba(0,0,0,0.45); margin-top: 0.15rem; }
[data-bs-theme='dark'] .wa-msg-meta { color: rgba(255,255,255,0.45); }
.wa-msg-tipo { margin-bottom: 0.2rem; }

/* Hora sobreposta na mídia/mapa (faixa gradiente + hora branca à direita). */
.wa-msg-img-wrap, .wa-msg-video-wrap { position: relative; }
.wa-meta-overlay {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  color: #fff; font-size: 0.68rem; line-height: 1;
  padding: 20px 8px 5px;
  background: linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 45%, transparent 100%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.65);
  pointer-events: none;
}
.wa-meta-overlay i { font-size: 0.85rem; }
/* Variante p/ cards de corpo claro (link/enquete/áudio): hora na linha de baixo. */
.wa-meta-overlay--sub {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  font-size: 0.68rem; line-height: 1; color: var(--vz-secondary-color, #8696a0);
  padding: 16px 0.6rem 0.35rem;
  background: linear-gradient(to top, var(--vz-tertiary-bg) 20%, transparent 100%);
  pointer-events: none; border-radius: 0 0 8px 8px;
}
[data-bs-theme='dark'] .wa-meta-overlay--sub { color: #8696a0; }
.wa-msg-bubble--out .wa-meta-overlay--sub { background: linear-gradient(to top, color-mix(in srgb, var(--vz-primary, #971219) 13%, #fff) 20%, transparent 100%); }
[data-bs-theme='dark'] .wa-msg-bubble--out .wa-meta-overlay--sub { background: linear-gradient(to top, color-mix(in srgb, var(--vz-primary, #971219) 42%, #1b2429) 20%, transparent 100%); }
.wa-meta-overlay--sub i { font-size: 0.8rem; }
/* Reserva o rodapé do balão pro overlay --sub. */
.wa-msg-bubble--metasub { padding-bottom: 1.05rem; }
.wa-msg-bubble--card.wa-msg-bubble--metasub { padding-bottom: 1.05rem; }

/* Enquete / Teste — MESMO layout dos grupos (classes wa-enquete-*). Cores por
   token pra funcionar sobre os 4 fundos (entrada/saída × claro/escuro). */
.wa-enquete {
  border: 1px solid var(--vz-border-color);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.35rem;
  background: color-mix(in srgb, var(--vz-body-color) 5%, transparent);
}
.wa-enquete-cab {
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em;
  opacity: 0.65; margin-bottom: 0.35rem;
}
.wa-enquete-pergunta { font-weight: 600; margin-bottom: 0.6rem; word-break: break-word; }
.wa-enquete-opcoes { list-style: none; margin: 0; padding: 0; }
.wa-enquete-opcoes li { padding: 0.35rem 0; font-size: 0.9rem; }
.wa-enquete-op-linha {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.5rem; margin-bottom: 0.2rem;
}
.wa-enquete-op-num { font-size: 0.8rem; opacity: 0.7; flex-shrink: 0; }
.wa-enquete-correta { color: var(--vz-success, #0ab39c); margin-left: 0.25rem; }
.wa-enquete-barra {
  height: 6px; border-radius: 4px; overflow: hidden;
  background: color-mix(in srgb, var(--vz-body-color) 12%, transparent);
}
.wa-enquete-barra-fill {
  height: 100%; border-radius: 4px;
  background: var(--vz-success, #0ab39c);
  transition: width 0.3s ease;
}
.wa-enquete-rodape {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 0.72rem; opacity: 0.75; margin-top: 0.5rem;
}

.wa-msg-bubble--midia { padding: 0.25rem 0.25rem 0.3rem; max-width: 360px; }
.wa-msg-bubble--midia .wa-msg-conteudo { padding: 0 0.45rem; }
.wa-msg-bubble--midia .wa-msg-meta { padding: 0 0.5rem 0; }

/* Cards não-texto (link, localização, enquete): largura padrão do balão e quase
   rentes às bordas — igual WhatsApp Web / grupos. */
.wa-msg-bubble--card { width: 330px; max-width: 100%; padding: 0.3rem 0.3rem 0.35rem; }
.wa-msg-bubble--card .wa-link-preview { max-width: none; width: 100%; }
.wa-msg-bubble--card .wa-msg-conteudo,
.wa-msg-bubble--card .wa-msg-meta { padding-left: 0.4rem; padding-right: 0.4rem; }

.wa-msg-media { display: flex; justify-content: center; }
.wa-msg-img { max-width: 100%; max-height: 320px; border-radius: 6px; cursor: zoom-in; display: block; }
.wa-msg-video { max-width: 100%; max-height: 320px; border-radius: 6px; display: block; }
.wa-msg-img-wrap { position: relative; display: inline-block; text-decoration: none; border-radius: 6px; overflow: hidden; }
.wa-msg-img-zoom {
  position: absolute; top: 8px; right: 8px;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(0,0,0,0.55); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; opacity: 0; transition: opacity 0.15s, background 0.15s;
  pointer-events: none;
}
.wa-msg-img-wrap:hover .wa-msg-img-zoom { opacity: 1; }
.wa-msg-img-wrap:hover .wa-msg-img-zoom:hover { background: rgba(0,0,0,0.75); }

.wa-msg-video-wrap { position: relative; display: inline-block; }
.wa-msg-video-play {
  position: absolute; inset: 0; margin: auto;
  width: 64px; height: 64px;
  border-radius: 50%; border: 0;
  background: rgba(0,0,0,0.55); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem; cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}
.wa-msg-video-play:hover { background: rgba(0,0,0,0.75); transform: scale(1.05); }
.wa-msg-sticker { max-width: 160px; max-height: 160px; }
.wa-msg-audio-wrap { min-width: 240px; padding: 0.2rem 0.1rem; }

.wa-msg-audio-wrap :deep(.btn-play) { width: 30px; height: 30px; background: #54656f !important; color: #fff !important; box-shadow: none; }
.wa-msg-audio-wrap :deep(.audio-player.invertido .btn-play) { background: #fff !important; color: #54656f !important; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.btn-play) { background: #aebac1 !important; color: #111b21 !important; }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.audio-player.invertido .btn-play) { background: #aebac1 !important; color: #111b21 !important; }
.wa-msg-audio-wrap :deep(.waveform) { height: 22px; gap: 2px; }
.wa-msg-audio-wrap :deep(.bar) { background: rgba(0,0,0,0.15); border-radius: 2px; }
.wa-msg-audio-wrap :deep(.bar.ativa) { background: #54656f; }
.wa-msg-audio-wrap :deep(.audio-player.invertido .bar) { background: rgba(0,0,0,0.18); }
.wa-msg-audio-wrap :deep(.audio-player.invertido .bar.ativa) { background: #54656f; }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.bar) { background: rgba(255,255,255,0.18); }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.bar.ativa) { background: #aebac1; }

.wa-msg-doc { padding: 0.25rem; }
.wa-msg-doc a { background: rgba(0,0,0,0.04); color: inherit; border-radius: 6px; padding: 0.6rem 0.75rem; }
[data-bs-theme='dark'] .wa-msg-doc a { background: rgba(255,255,255,0.06); color: #e9edef; }
.wa-msg-doc a:hover { background: rgba(0,0,0,0.07); }
[data-bs-theme='dark'] .wa-msg-doc a:hover { background: rgba(255,255,255,0.1); }
.wa-msg-doc-icon { font-size: 1.7rem; opacity: 0.8; }

.wa-msg-falta-midia {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.5rem 0.6rem; background: rgba(0,0,0,0.04);
  border-radius: 6px; font-size: 0.85rem;
  color: var(--vz-text-muted, #878a99);
}
[data-bs-theme='dark'] .wa-msg-falta-midia { background: rgba(255,255,255,0.06); }
.wa-msg-falta-midia button { color: var(--vz-primary, #0d6efd) !important; }

.wa-link-preview {
  display: flex; flex-direction: column; margin: 0.1rem 0 0.35rem;
  border-radius: 8px; overflow: hidden;
  background: rgba(0,0,0,0.05); color: inherit;
  text-decoration: none !important; max-width: 330px;
  transition: background 0.15s;
}
[data-bs-theme='dark'] .wa-link-preview { background: rgba(255,255,255,0.07); }
.wa-link-preview:hover { background: rgba(0,0,0,0.08); }
[data-bs-theme='dark'] .wa-link-preview:hover { background: rgba(255,255,255,0.11); }
.wa-link-thumb { width: 100%; aspect-ratio: 1.91 / 1; max-height: 188px; object-fit: cover; display: block; background: rgba(0,0,0,0.06); }
[data-bs-theme='dark'] .wa-link-thumb { background: rgba(255,255,255,0.05); }
.wa-link-body { padding: 0.5rem 0.7rem 0.55rem; }
.wa-link-title {
  font-weight: 600; font-size: 0.9rem; line-height: 1.3; margin-bottom: 0.1rem;
  color: var(--vz-body-color, #1a1d21);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-desc {
  font-size: 0.8rem; color: var(--vz-secondary-color, #878a99); line-height: 1.3; margin-bottom: 0.15rem;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-url {
  font-size: 0.72rem; color: var(--vz-secondary-color, #878a99); text-transform: lowercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Localização: mini-mapa com tiles OSM (sem chave), igual aos grupos. */
.wa-loc-map {
  position: relative; width: 100%; height: 140px;
  display: flex; align-items: center; justify-content: center;
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #cfe8d6, #a9d2c3);
  overflow: hidden;
}
[data-bs-theme='dark'] .wa-loc-map {
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #223129, #1a2b33);
}
.wa-loc-tile { position: absolute; width: 256px; height: 256px; max-width: none; user-select: none; pointer-events: none; }
.wa-loc-pin {
  position: relative; z-index: 1; font-size: 2.2rem; color: #e11d48;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35)); transform: translateY(-4px);
}
.wa-loc-preview .wa-link-url { color: var(--vz-secondary-color, #878a99); }

.min-w-0 { min-width: 0; }

/* ----- Painel info ----- */
.wa-info-panel {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 340px;
  background: var(--vz-card-bg, #fff);
  border-left: 1px solid var(--vz-border-color);
  display: flex; flex-direction: column;
  z-index: 10;
  box-shadow: -4px 0 12px rgba(0,0,0,0.1);
}
.wa-info-header {
  padding: 0.75rem 1rem;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-light);
}
.wa-info-body { padding: 1rem; overflow-y: auto; flex: 1; }
.wa-info-avatar {
  width: 96px; height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, #128c7e, #25d366);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 2.6rem; overflow: hidden;
}
.wa-info-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.wa-foto-btn {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 30px;
  border: 0;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 0.95rem;
}
.wa-foto-btn:hover { background: rgba(0, 0, 0, 0.6); }
.wa-foto-btn:disabled { opacity: 0.7; cursor: default; }

.slide-enter-active, .slide-leave-active { transition: transform 0.2s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }

/* ----- Lightbox ----- */
.wa-lightbox {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.92);
  display: flex; align-items: center; justify-content: center;
  z-index: 1090; padding: 1.5rem;
}
.wa-lightbox-img { max-width: 95vw; max-height: 95vh; border-radius: 4px; }
.wa-lightbox-video { max-width: 95vw; max-height: 95vh; background: #000; border-radius: 4px; outline: none; }
.wa-lightbox-fechar, .wa-lightbox-baixar {
  position: absolute; top: 1rem;
  width: 44px; height: 44px;
  border-radius: 50%; border: 0;
  background: rgba(255,255,255,0.12); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; cursor: pointer; text-decoration: none;
  transition: background 0.15s;
}
.wa-lightbox-fechar { right: 1rem; }
.wa-lightbox-baixar { right: 4rem; }
.wa-lightbox-fechar:hover, .wa-lightbox-baixar:hover { background: rgba(255,255,255,0.25); color: #fff; }

/* ----- Modais ----- */
.modal-can-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1080;
}
.modal-can-content {
  background: var(--vz-card-bg, #fff);
  color: var(--vz-body-color, inherit);
  border-radius: 12px;
  width: 100%; max-width: 480px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.35);
  border: 1px solid var(--vz-border-color, transparent);
}
[data-bs-theme="dark"] .modal-can-content { background: var(--vz-card-bg, #2a2f3a); }
.modal-can-header, .modal-can-footer {
  padding: 1rem 1.25rem;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid var(--vz-border-color);
}
.modal-can-footer { border-bottom: none; border-top: 1px solid var(--vz-border-color); justify-content: flex-end; gap: 0.5rem; }
.modal-can-body { padding: 1.25rem; }
/* Picker de emoji dentro do modal de enquete/teste. */
.modal-emoji-btn {
  border: 0; background: transparent; color: var(--vz-secondary-color, #878a99);
  width: 34px; height: 34px; flex-shrink: 0; border-radius: 0.375rem;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.15rem; cursor: pointer; transition: background 0.15s, color 0.15s;
}
.modal-emoji-btn:hover:not(:disabled) { background: var(--vz-tertiary-bg, #f3f6f9); color: var(--vz-primary); }
.modal-emoji-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.modal-emoji-pop {
  margin-top: 0.4rem; padding: 0.4rem;
  background: var(--vz-body-bg, #fff); border: 1px solid var(--vz-border-color); border-radius: 0.5rem;
  display: grid; grid-template-columns: repeat(10, 1fr); gap: 0.1rem;
  max-height: 148px; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}
.modal-emoji-item {
  background: transparent; border: 0; font-size: 1.2rem; cursor: pointer;
  padding: 0.2rem 0; border-radius: 0.25rem; line-height: 1;
}
.modal-emoji-item:hover { background: var(--vz-tertiary-bg, #f3f6f9); }
[data-bs-theme="dark"] .modal-emoji-pop { background: #1c2128; border-color: #2a3038; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
[data-bs-theme="dark"] .modal-emoji-item:hover { background: #262c34; }
[data-bs-theme="dark"] .modal-emoji-btn:hover:not(:disabled) { background: #262c34; color: #ff9aa5; }

/* ----- FAB "Criar canal" (mobile) ----- */
.canais-fab {
  position: fixed;
  right: 1.25rem;
  /* Acima da barra de navegação inferior (58px) + respiro + safe-area. */
  bottom: calc(58px + 1rem + env(safe-area-inset-bottom, 0px));
  z-index: 1045;
  width: 56px; height: 56px;
  border: 0; border-radius: 50%;
  background: var(--vz-primary, #971219);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.7rem; line-height: 1;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
}
.canais-fab:hover { background: var(--brand-primary-hover, #7a0e14); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35); transform: translateY(-1px); }
.canais-fab:active { transform: translateY(0); }
.canais-fab:disabled { opacity: 0.55; box-shadow: none; cursor: default; }
</style>

<style>
/* Modo full-viewport — mesma técnica do Grupos/Atendimento */
body.canais-sem-footer { overflow: hidden; }
body.canais-sem-footer .footer-pcdob,
body.canais-sem-footer .footer { display: none !important; }
body.canais-sem-footer .main-content {
  height: 100vh;
  height: 100dvh; /* mobile: desconta a barra do navegador — composer sempre visível */
  display: flex; flex-direction: column; overflow: hidden;
}
body.canais-sem-footer .page-content {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  overflow: hidden; padding-bottom: 0;
}
body.canais-sem-footer .page-content > .container-fluid {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  overflow: hidden;
}
body.canais-sem-footer .page-content > .container-fluid > div:first-child { flex-shrink: 0; }

/* ===== Mobile: comportamento de app nativo (estilo WhatsApp) ===== */
@media (max-width: 767.98px) {
  /* Full-bleed: remove o respiro lateral e esconde o cabeçalho de página. */
  body.canais-sem-footer .page-content,
  body.canais-sem-footer .page-content > .container-fluid {
    padding-left: 0; padding-right: 0;
  }
  body.canais-sem-footer .page-content > .container-fluid > div:first-child { display: none; }
  /* Cola a lista logo abaixo da topbar (mesma altura do header de conversa). */
  body.canais-sem-footer .page-content { padding-top: 60px; }

  /* Conversa em fullscreen: esconde a topbar do app e cola o conteúdo no topo. */
  body.canais-chat #page-topbar { display: none; }
  body.canais-chat.canais-sem-footer .page-content { padding-top: 0 !important; }
}
</style>
